import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import QueryState from '@/components/ui/QueryState'
import Skeleton from '@/components/ui/Skeleton'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TextLink from '@/components/ui/TextLink'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import { useIncome, useTransactions } from '@/features/portfolio/api/queries'
import type { TransactionHistory } from '@/schemas/portfolio'
import BonusRightsRecords from '@/screens/portfolio/components/income/BonusRightsRecords'
import BonusAndRightsCard from '@/screens/portfolio/components/income/BonusAndRightsCard'
import ComingUpCard from '@/screens/portfolio/components/income/ComingUpCard'
import {
  dividendTotals,
  currentFiscalYear,
  fiscalYearLabel,
  fiscalYearStart,
  incomeTotals,
  inFiscalYear,
  rightsFromPurchases,
} from '@/screens/portfolio/components/income/deriveIncome'
import DividendRecordsTable from '@/screens/portfolio/components/income/DividendRecordsTable'
import DividendSummaryCard from '@/screens/portfolio/components/income/DividendSummaryCard'
import YearSummaryCard from '@/screens/portfolio/components/income/YearSummaryCard'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio/income')({ component: IncomeScreen })

type IncomeTab = 'dividends' | 'bonus' | 'rights' | 'closures'

/** How many "Coming up" events show before "See all". */
const UPCOMING_AT_REST = 2

const TAB_OPTIONS: readonly TabBarOption[] = [
  { value: 'dividends', label: 'Dividends' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'rights', label: 'Rights' },
  { value: 'closures', label: 'Book Closures' },
]

/**
 * Portfolio · Income (V2 `4236:77663`, V1 997:27564).
 *
 * ── Two reads, and only one of them gates the screen ───────────────────────
 *
 * `GET /transactions/` is the gate: `cashDividends` for the dividend table
 * and summary, `bonusShares` and `purchases[kind="RIGHT"]` for the
 * bonus-and-rights card. `summary.dividend_received` is NOT read here even
 * though the brief names it as a valid source for the same figure: it is the
 * same `Sum(cash_dividend)` as summing `cashDividends` locally, and reading
 * it via `usePortfolioSummary` would add a second request AND a
 * null-narrowing trap — that hook maps the WHOLE summary to `null` when
 * `holdingsCount === 0`, which would hide a real dividend history for a
 * member who has since sold every holding.
 *
 * `GET /income/` is fetched alongside it and is NOT part of that gate. It
 * feeds "Coming up", the Book-closures tab, and the dividend table's
 * per-company Tax/Net — all shown gated on their own pending/error state, the
 * same shape `OverviewScreen` uses for `/profit-loss/`. A slower or failing
 * calendar must not hold up, or blank, a dividend ledger that is already in
 * hand — `QueryState` below only ever gates on `tx`.
 *
 * ── Four sub-tabs, and what each one actually governs ───────────────────────
 *
 * V2 draws Dividends / Bonus / Rights / Book Closures above the body,
 * replacing the old "Dividend records" h2 kicker. The strip's old first tab,
 * "Transactions", which only left for Activity, is gone (2026-11-03 mock):
 * Activity is a page of its own in the family header. All four are views of
 * this page: Bonus and Rights
 * reuse `BonusAndRightsCard` filtered to one kind; Book Closures reuses
 * `ComingUpCard`'s already-built rows, narrowed to the ones that actually
 * carry a closure date. Dividends (the design's own default, "on") is the
 * ORIGINAL summary-card-plus-table content, unchanged.
 *
 * The tab strip governs ONLY that swappable slot. "Coming up", "Bonus and
 * rights" and the all-time summary stay a separate, un-tabbed section below
 * it on every tab, exactly as V1 built them and as the one captured V2 frame
 * (the Dividends tab, "on") shows them — there is no second frame proving a
 * different tab hides or duplicates that section, so nothing here assumes one.
 *
 * ── Dividend table: Tax/Net go from all-gated to real-where-honest ──────────
 *
 * `GET /income/`'s `received[]` already carries `gross`/`tax`/`net` per
 * COMPANY — unused until now. `DividendRecordsTable` fills a row's Tax/Net
 * from it exactly when that company has ONE recorded payment (no
 * attribution to guess); the Totals row uses the report's own totals
 * unconditionally, and so does the summary card's "Tax deducted" (`totalTax`).
 * Eligible-quantity, per-payment rate/paid-date and per-record status have no
 * field anywhere, so the table draws no column for them. The mock's closing
 * Mitra banner is not drawn, as on every Portfolio page.
 */
function IncomeScreen() {
  const shareholderId = useActiveShareholderId()
  const tx = useTransactions(shareholderId)
  const incomeQuery = useIncome(shareholderId)
  const [tab, setTab] = useState<IncomeTab>('dividends')
  // "See all 4" / "Show fewer" (mock `inc-seeall`): two events at rest, every one when open.
  const [showAllUpcoming, setShowAllUpcoming] = useState(false)
  const idBase = useId()

  return (
    <ScreenLayout header={<PortfolioFamilyHeader tab="income" compactAction />} pageTitle="Income">
      <QueryState
        query={tx}
        isEmpty={(data: TransactionHistory) =>
          data.cashDividends.length === 0 &&
          data.bonusShares.length === 0 &&
          rightsFromPurchases(data.purchases).length === 0
        }
        skeleton={
          <>
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </>
        }
        loadingLabel="Loading your income…"
        size="page"
        empty={{
          title: 'No income recorded yet',
          description: 'Dividends, bonus shares and rights you record will show up here.',
        }}
        failed={{
          title: 'Income did not load',
          description: failureMessage(portfolioFailure(tx.error) ?? { kind: 'failed' }),
        }}
        className="flex flex-col gap-6"
      >
        {(data) => {
          const rights = rightsFromPurchases(data.purchases)
          const dividends = dividendTotals(data.cashDividends)
          // The year strip is this fiscal year's (mock "This year so far, FY 2083-84").
          const fiscalYear = currentFiscalYear()
          const totals = incomeTotals(
            inFiscalYear(data.cashDividends, fiscalYear),
            inFiscalYear(data.bonusShares, fiscalYear),
            inFiscalYear(rights, fiscalYear),
          )
          const taxCoversYear = data.cashDividends.every(
            (row) => fiscalYearStart(row.date) === fiscalYear,
          )
          const upcoming = incomeQuery.data?.upcoming ?? []
          const closures = upcoming.filter((row) => row.bookClosureDate !== null)

          return (
            <>
              <TabBar
                options={TAB_OPTIONS}
                value={tab}
                onChange={(next) => {
                  setTab(next as IncomeTab)
                }}
                ariaLabel="Income sections"
                idBase={idBase}
              />

              <TabPanel idBase={idBase} value={tab}>
                {tab === 'dividends' ? (
                  <section className="flex flex-col gap-3">
                    <h2 className="text-h-m">Dividend records</h2>
                    <DividendSummaryCard
                      count={dividends.count}
                      gross={dividends.gross}
                      split={
                        incomeQuery.data
                          ? {
                              gross: incomeQuery.data.totalGross,
                              tax: incomeQuery.data.totalTax,
                              net: incomeQuery.data.totalNet,
                            }
                          : undefined
                      }
                    />
                    <DividendRecordsTable
                      rows={data.cashDividends}
                      gross={dividends.gross}
                      received={incomeQuery.data?.received}
                      totals={
                        incomeQuery.data
                          ? { tax: incomeQuery.data.totalTax, net: incomeQuery.data.totalNet }
                          : undefined
                      }
                    />
                  </section>
                ) : tab === 'bonus' || tab === 'rights' ? (
                  <BonusRightsRecords
                    kind={tab}
                    bonusRows={tab === 'bonus' ? data.bonusShares : []}
                    rightsRows={tab === 'rights' ? rights : []}
                  />
                ) : (
                  <section className="flex flex-col gap-3">
                    <h2 className="text-h-m">Book closures</h2>
                    <ComingUpCard
                      rows={closures}
                      pending={incomeQuery.isPending}
                      error={incomeQuery.isError}
                      onRetry={() => {
                        void incomeQuery.refetch()
                      }}
                      retrying={incomeQuery.isFetching}
                    />
                  </section>
                )}
              </TabPanel>
              {/* `@2xl` (672px) and NOT the old `lg:grid-cols-[1fr_352px]`. That
                  split was sized for the pre-rail 1112px body; inside the main
                  column's `@container` — 253/445/509 stacked, 285…909 beside —
                  a fixed 352px second track leaves the first one at nothing.
                  See `ScreenLayout`'s doc comment. Stays un-tabbed — see this
                  file's own doc comment for why. */}
              <div className="grid grid-cols-1 gap-6 @2xl:grid-cols-2">
                <section className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-h-m">Coming up</h2>
                    {upcoming.length > UPCOMING_AT_REST ? (
                      <TextLink
                        aria-expanded={showAllUpcoming}
                        onClick={() => {
                          setShowAllUpcoming((open) => !open)
                        }}
                      >
                        {showAllUpcoming ? 'Show fewer' : `See all ${String(upcoming.length)}`}
                      </TextLink>
                    ) : null}
                  </div>
                  <ComingUpCard
                    rows={showAllUpcoming ? upcoming : upcoming.slice(0, UPCOMING_AT_REST)}
                    pending={incomeQuery.isPending}
                    error={incomeQuery.isError}
                    onRetry={() => {
                      void incomeQuery.refetch()
                    }}
                    retrying={incomeQuery.isFetching}
                    className="flex-1"
                  />
                </section>

                <section className="flex flex-col gap-3">
                  <h2 className="text-h-m">Bonus and rights</h2>
                  <BonusAndRightsCard
                    bonusRows={data.bonusShares}
                    rightsRows={rights}
                    className="flex-1"
                  />
                </section>
              </div>

              <YearSummaryCard
                totals={totals}
                fiscalYear={fiscalYear === null ? null : fiscalYearLabel(fiscalYear)}
                taxCoversYear={taxCoversYear}
                tax={incomeQuery.data?.totalTax}
                taxPending={incomeQuery.isPending}
                taxError={incomeQuery.isError}
              />
            </>
          )
        }}
      </QueryState>
    </ScreenLayout>
  )
}
